import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../hooks/useAuthCompat";
import { Plus, ArrowRight } from "lucide-react";
import { useApi, publicApi } from "../hooks/useApi";
import { useCart } from "../context/CartContext";
import { getBundleSavings } from "../lib/bundleSavings";
import { HeroCarousel } from "../components/home/HeroCarousel";
import { MuscleSchoolBanner } from "../components/home/MuscleSchoolBanner";

const HERO_IMAGE = "/hero/expedition-team.webp";
const GOLD_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_d18f6363-3058-4bfd-b277-f958917ab691/artifacts/gocf68d7_60D7CC8F-C7E7-4D49-A962-78B54C3E34CC.jpeg";

const CAROUSEL_CARDS = [
  {
    testId: "home-carousel-card-podcast",
    title: "ENDURE BEYOND PODCAST",
    description: "Conversations on human performance, adaptation, and what comes next.",
    alt: "The Endure Beyond expedition team in frontier gear beside their rover at sunrise",
    link: "/listen",
    image: HERO_IMAGE,
  },
  {
    testId: "home-carousel-card-muscle-school",
    title: "THE MUSCLE SCHOOL",
    description: "Seven Acts. 106 Lessons. The architecture of durable human muscle.",
    alt: "The Muscle School — an institution of durable human capability",
    link: "/themuscleschool",
    image: "/tms/hero.jpg",
  },
  {
    testId: "home-carousel-card-books",
    title: "ENDURE BEYOND BOOKS",
    description: "The MODELS Series and beyond — build more, waste less, go further.",
    alt: "Muscle Models, Efficiency Models and Off-World Models hardcover books",
    link: "/books",
    image: "/carousel/books-card.jpg",
  },
  {
    testId: "home-carousel-card-gold",
    title: "GOLD & GOLD PRO",
    description: "Member-only briefings, the Weekly Signal, and the full Endure Beyond archive.",
    alt: "The Endure Beyond Gold Vault",
    link: "/gold",
    image: GOLD_HERO_IMAGE,
  },
];

function SectionHeading({ eyebrow, title, to, cta }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-textSecondary mb-2">{eyebrow}</p>
        <h2 className="text-2xl md:text-3xl font-medium">{title}</h2>
      </div>
      {to && (
        <Link to={to} data-testid={`section-cta-${eyebrow.toLowerCase().replace(/\s/g, "-")}`} className="text-sm uppercase tracking-wide text-white/60 hover:text-white flex items-center gap-1 shrink-0">
          {cta || "View all"} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const api = useApi();
  const navigate = useNavigate();
  const { isSignedIn } = useUser();
  const { addItem } = useCart();
  const { data: latestEp } = useQuery({ queryKey: ["latest-ep"], queryFn: async () => (await publicApi.get("/episodes/latest")).data });
  const { data: books } = useQuery({ queryKey: ["books-home"], queryFn: async () => (await publicApi.get("/books")).data });
  const { data: editions } = useQuery({ queryKey: ["audio-home"], queryFn: async () => (await publicApi.get("/audio")).data });
  const { data: library } = useQuery({
    queryKey: ["library-home"], queryFn: async () => (await api.get("/library")).data,
    enabled: !!isSignedIn,
  });

  const continuing = library?.books?.find((b) => b.progress) || null;
  const ownedIds = new Set((library?.books || []).map((b) => b.id));

  return (
    <div data-testid="home-page">
      <h1 className="sr-only">Endure Beyond — Podcast, Books, The Muscle School, and Gold membership</h1>
      <MuscleSchoolBanner />

      <HeroCarousel cards={CAROUSEL_CARDS} />

      <section data-testid="home-about-section" className="py-10 md:py-20 flex flex-col items-center justify-center bg-background px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-subtitle text-sm uppercase tracking-widest text-textSecondary mb-4">About Endure Beyond</p>
          <p data-testid="home-about-copy" className="font-subtitle text-base leading-[1.6] md:text-display-md md:leading-[1.5] text-foreground">
            Endure Beyond Media explores what it takes to perform, adapt, and advance in an increasingly complex world. Through podcasts, books, and applied education, we connect ideas in human performance, decision-making, and exploration with the knowledge and capabilities needed to go further—on Earth and beyond.
          </p>
          <p className="font-display text-sm uppercase tracking-[0.25em] text-white/70 mt-8" data-testid="home-slogan">
            Built for Earth. Ready for Beyond.
          </p>
        </div>
      </section>

      {books && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 md:py-12 border-t border-white/10">
          <SectionHeading eyebrow="Read" title="Browse the MODELS Series" to="/books" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {books.slice(0, 7).map((b) => (
              <div key={b.id} data-testid={`home-book-card-${b.slug}`} className="group relative">
                <Link to={`/read/${b.id}`} className="block">
                  <img src={b.cover_image} alt={b.title} className="w-full aspect-square object-cover group-hover:opacity-80 transition-opacity duration-200" />
                  {b.category === "Bundle" && (
                    <span data-testid={`bundle-badge-${b.slug}`} className="absolute top-2 right-2 bg-black/85 border border-white/30 text-white text-[9px] uppercase tracking-wide px-1.5 py-0.5">Bundle</span>
                  )}
                  <p className="text-xs mt-2 truncate">{b.title}</p>
                  <p className="text-[11px] text-white/70 mt-0.5">${b.price.toFixed(2)}</p>
                  {getBundleSavings(b, books) != null && (
                    <p data-testid={`bundle-savings-${b.slug}`} className="text-[10px] text-brandAccent mt-0.5">Save ${getBundleSavings(b, books).toFixed(0)}</p>
                  )}
                </Link>
                <div className="flex gap-1.5 mt-2">
                  {ownedIds.has(b.id) ? (
                    <button
                      data-testid={`open-reader-${b.slug}`}
                      onClick={() => navigate(`/reader/${b.id}`)}
                      className="flex-1 text-[9px] uppercase tracking-wide border border-white/40 py-1.5 hover:border-white transition-colors duration-200"
                    >
                      Owned — Open
                    </button>
                  ) : b.sale_blocked ? (
                    <span data-testid={`sale-blocked-${b.slug}`} className="flex-1 text-[9px] uppercase tracking-wide text-white/50 border border-white/15 py-1.5 text-center">
                      Unavailable
                    </span>
                  ) : (
                    <>
                      <button
                        data-testid={`quick-buy-${b.slug}`}
                        onClick={() => navigate(`/checkout?buyNow=${b.id}`)}
                        className="flex-1 text-[9px] uppercase tracking-wide bg-white text-black py-1.5 hover:bg-white/80 transition-colors duration-200"
                      >
                        Quick Buy
                      </button>
                      <button
                        data-testid={`add-to-cart-${b.slug}`}
                        aria-label={`Add ${b.title} to cart`}
                        onClick={() => addItem(b.id, b.title)}
                        className="border border-white/30 px-2 hover:border-white transition-colors duration-200"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {isSignedIn && continuing && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 md:py-12 border-t border-white/10">
          <SectionHeading eyebrow="Continue Your Journey" title={continuing.title} to="/library" cta="My Library" />
          <Link to={`/reader/${continuing.id}`} data-testid="continue-journey-card" className="flex gap-6 items-center bg-surface border border-border p-6 hover:border-white/30 transition-colors duration-200">
            <img src={continuing.cover_image} alt="" className="w-20 h-20 object-cover" />
            <div>
              <p className="text-white/75 text-sm">Pick up where you left off</p>
              <p className="text-lg mt-1">{continuing.title}</p>
              <span className="inline-block mt-3 text-xs uppercase tracking-wide text-brandAccent">Continue Reading →</span>
            </div>
          </Link>
        </section>
      )}

      {latestEp && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 md:py-12 border-t border-white/10">
          <SectionHeading eyebrow="Listen" title="Newest Verified Episode" to="/listen" />
          <Link to={`/listen/${latestEp.id}`} data-testid="home-latest-episode-card" className="flex flex-col md:flex-row gap-6 bg-surface border border-border p-6 hover:border-white/30 transition-colors duration-200">
            <img src={latestEp.image_url} alt="" className="w-full md:w-48 h-48 object-cover" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-textSecondary mb-2">Episode {latestEp.episode_number || "—"}</p>
              <h3 className="text-xl md:text-2xl">{latestEp.title}</h3>
              <p className="text-white/75 text-sm mt-3 line-clamp-3" dangerouslySetInnerHTML={{ __html: latestEp.description }} />
            </div>
          </Link>
        </section>
      )}

      {editions && editions.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 md:py-12 border-t border-white/10">
          <SectionHeading eyebrow="Audio" title="Deluxe Author Commentary Editions" to="/books" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {editions.slice(0, 3).map((e) => (
              <Link key={e.id} to={`/audio/${e.id}`} data-testid={`home-audio-card-${e.slug}`} className="flex gap-4 bg-surface border border-border p-4 hover:border-white/30 transition-colors duration-200">
                <img src={e.cover_image} alt="" className="w-16 h-16 object-cover" />
                <div>
                  <p className="text-sm">{e.title}</p>
                  <p className="text-[11px] text-white/70 mt-2 uppercase tracking-wide">Coming Soon</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
